import React, { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from 'src/services/firebase-service';
import logging from 'src/services/logging';
import {
  Backdrop,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { GameResult } from 'src/models';
import _ from 'lodash';

type ResultsTableProps = {
  season: number;
  playoffSeriesLetter: string;
};

const ResultsTable = ({
  season,
  playoffSeriesLetter,
}: ResultsTableProps): JSX.Element => {
  const [fetchingResults, setFetchingResults] = useState<boolean>(false);
  const [results, setResults] = useState<GameResult[]>([]);

  // TODO: can this be moved into ResultsAccordion to reduce reads?
  const fetchResults = useCallback(async () => {
    try {
      logging.info(
        `Fetching results for series ${playoffSeriesLetter}`,
        'ResultsTable'
      );
      setFetchingResults(true);
      const q = query(
        collection(db, 'gameResults'),
        where('season', '==', season),
        where('playoffSeriesLetter', '==', playoffSeriesLetter)
      );
      const snapshot = await getDocs(q);
      const results: GameResult[] = [];
      snapshot.forEach((game) => {
        results.push(game.data() as GameResult);
      });
      const sortedResults = _.orderBy(results, ['gamePk'], ['asc']);
      setResults(sortedResults);
    } catch (error) {
      logging.error(error, 'ResultsTable');
    } finally {
      setFetchingResults(false);
    }
  }, [playoffSeriesLetter, season]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  if (fetchingResults) {
    return (
      <Backdrop open={fetchingResults}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ width: '100%' }} aria-label='simple table' size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Score</TableCell>
            <TableCell align='right'>Game Winning Goal</TableCell>
            <TableCell align='right'>Game</TableCell>
            <TableCell align='right'>Winner</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((game) => (
            <TableRow
              key={game.gamePk}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component='th' scope='row'>
                {game.awayScore > game.homeScore
                  ? `${game.awayScore} - ${game.homeScore}`
                  : `${game.homeScore} - ${game.awayScore}`}
              </TableCell>
              <TableCell align='right'>{game.gwgScorer.fullName}</TableCell>
              <TableCell align='right'>{game.numOfGames}</TableCell>
              <TableCell align='right'>
                {game.winningTeam.abbreviation}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ResultsTable;
